import { useState, useMemo, useRef, useEffect } from "react";
import { useSpring, animated } from "@react-spring/web";
import type { ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAgentRunStore } from "@/stores/agentRunStore";
import {
  CalendarClock, ChevronLeft, Clock, FileText, Pencil, Play, Settings2, Wrench,
} from "lucide-react";
import { CreateAgentPanel } from "@/components/organisms/studio/CreateAgentPanel";
import { TermWindow } from "@/components/organisms/studio/TermWindow";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { useAgent } from "@/hooks/studio-platform";
import { agentOutcome } from "@/lib/studio/agentOutcomes";
import {
  activeAgentTools,
  isRetiredAgentTool,
  RETIRED_AGENT_TOOL_REASON,
} from "@/lib/studio/agentTools";
import { formatRelative, runErrorLabel } from "@/lib/studio/utils";
import { progressFromEvents, eventLabel } from "@/components/organisms/studio/AgentRunProgress";
import { AgentRunTab } from "./AgentRunTab";
import type { AgentRecord, RunStatusValue } from "@/services/studio/types";


function durationLabel(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── Tab types ─────────────────────────────────────────────────────────────────
type WorkspaceTab = "overview" | "config" | "tools" | "runs" | "run";

// ── Page ──────────────────────────────────────────────────────────────────────
export function AgentWorkspace() {
  const { id } = useParams<{ id: string }>();
  const agentQ = useAgent(id);
  // Key the run-store by the RESOLVED agent id (what AgentRunTab uses), not the
  // raw URL param — the URL can be a UUID (e.g. from a Reports deep-link) while
  // the agent's canonical id is a slug. Using the param would mis-key the store
  // so setPhase/ensureEntry would target a different entry than AgentRunTab reads.
  const agentId = agentQ.data?.agent.id ?? id ?? "";
  const [activeTab, setActiveTab] = useState<WorkspaceTab>("overview");

  // Run state lives in the Zustand store so it survives route navigation
  const runEntry = useAgentRunStore((s) => s.runs.get(agentId));
  const runPhase = runEntry?.phase ?? "configure";
  const runEvents = runEntry?.events ?? [];

  // Historical run viewed from Run History tab (ephemeral UI nav — stays local)
  const [viewRunId, setViewRunId] = useState<string | null>(null);
  // Edit panel (user-owned agents only)
  const [editOpen, setEditOpen] = useState(false);

  // Ensure a store entry exists once agent data loads
  useEffect(() => {
    if (!agentQ.data) return;
    useAgentRunStore.getState().ensureEntry(agentId, agentQ.data.agent.name);
  }, [agentId, agentQ.data?.agent.id]);

  // Tab content entrance spring — triggered on every tab/phase transition
  const [contentSpring, contentSpringApi] = useSpring(() => ({
    opacity: 1,
    transform: "translateY(0px)",
    config: { tension: 380, friction: 28 },
  }));

  const triggerContentAnimation = () => {
    contentSpringApi.start({
      from: { opacity: 0, transform: "translateY(6px)" },
      to: { opacity: 1, transform: "translateY(0px)" },
    });
  };

  // Also animate when the run phase changes (stop button, run complete, etc.)
  const prevRunPhase = useRef(runPhase);
  useEffect(() => {
    if (prevRunPhase.current !== runPhase && activeTab === "run") {
      triggerContentAnimation();
    }
    prevRunPhase.current = runPhase;
  }, [runPhase]);

  const handleViewHistoricalRun = (runId: string) => {
    setViewRunId(runId);
    useAgentRunStore.getState().setPhase(agentId, "done");
    useAgentRunStore.getState().setEvents(agentId, []);
    setActiveTab("run");
    triggerContentAnimation();
  };

  // Deep-link: /agents/:id?run_id=<runId> opens that run in the Run tab's report
  // view (the same surface as Run History → "View report"). Reports route links
  // here so the report view is consistent with run history.
  const [searchParams] = useSearchParams();
  const runParam = searchParams.get("run_id");
  const openedRunParam = useRef<string | null>(null);
  useEffect(() => {
    if (!agentQ.data || !runParam) return;
    if (openedRunParam.current === runParam) return;
    openedRunParam.current = runParam;
    // Make sure the run-store entry exists before we flip it to the "done"
    // report view — setPhase is a no-op on a missing entry, which would leave
    // the Run tab stuck on the configure form.
    useAgentRunStore.getState().ensureEntry(agentId, agentQ.data.agent.name);
    handleViewHistoricalRun(runParam);
  }, [agentQ.data?.agent.id, runParam]);

  // After a hard refresh the AgentRunResumeManager reattaches live runs to the
  // store; when that happens land the user on the Run tab once so the step
  // cards are visible — not just the progress strip. The ref makes this fire at
  // most once per mount, so it never hijacks the tabs mid-session (the phase
  // only transitions INTO 'running' via startRun, which happens on the Run tab).
  const autoOpenedRunningTab = useRef(false);
  useEffect(() => {
    if (autoOpenedRunningTab.current || runParam) return;
    if (runPhase !== "running") return;
    autoOpenedRunningTab.current = true;
    setActiveTab("run");
  }, [runPhase, runParam]);

  const runProgress = useMemo(() => progressFromEvents(runEvents), [runEvents]);
  const lastRunLabel = runEvents.length > 0 ? eventLabel(runEvents[runEvents.length - 1]) : "";

  if (agentQ.isLoading) {
    return (
      <div className="flex h-full flex-col gap-3">
        {/* Main panel skeleton */}
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card">

          {/* Agent header skeleton */}
          <div className="shrink-0 border-b border-border bg-muted/20 px-5 py-4 space-y-2.5">
            {/* Row 1: breadcrumb + Run Agent button */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-12 rounded loading-parallax bg-muted-foreground/10" />
                <span className="text-muted-foreground/20 text-sm">/</span>
                <div className="h-5 w-48 rounded-md loading-parallax bg-muted-foreground/10" />
              </div>
              <div className="h-7 w-24 rounded-md loading-parallax bg-muted-foreground/10" />
            </div>
            {/* Row 2: badges + stats */}
            <div className="flex items-center gap-3">
              <div className="h-5 w-20 rounded-full loading-parallax bg-muted-foreground/10" />
              <div className="h-5 w-24 rounded-full border border-border/50 bg-muted-foreground/[0.04]" />
              <div className="ml-3 flex items-center gap-3">
                <div className="h-3 w-12 rounded loading-parallax bg-muted-foreground/[0.07]" />
                <div className="h-3 w-12 rounded loading-parallax bg-muted-foreground/[0.07]" />
                <div className="h-3 w-16 rounded loading-parallax bg-muted-foreground/[0.07]" />
              </div>
            </div>
          </div>

          {/* Tab strip skeleton */}
          <div className="flex shrink-0 items-center gap-1 border-b border-border px-3 py-0">
            {[80, 100, 140, 88].map((w, i) => (
              <div key={i} className="mx-2 my-2.5 h-3 rounded loading-parallax bg-muted-foreground/10" style={{ width: w }} />
            ))}
          </div>

          {/* Content skeleton — mirrors Overview 2-col layout */}
          <div className="flex-1 overflow-hidden p-5">
            <div className="grid gap-6 xl:grid-cols-[1fr_20rem] h-full">
              {/* Left column */}
              <div className="space-y-6">
                {/* Section: Goal */}
                <div className="space-y-2">
                  <div className="h-2.5 w-20 rounded loading-parallax bg-muted-foreground/[0.07]" />
                  <div className="h-3.5 w-full rounded loading-parallax bg-muted-foreground/10" />
                  <div className="h-3.5 w-5/6 rounded loading-parallax bg-muted-foreground/[0.07]" />
                </div>
                {/* Section: Expected Outcome */}
                <div className="space-y-2">
                  <div className="h-2.5 w-32 rounded loading-parallax bg-muted-foreground/[0.07]" />
                  <div className="h-5 w-24 rounded-full border border-border/50 bg-muted-foreground/[0.04]" />
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-9 w-full rounded-md border border-border/50 bg-muted-foreground/[0.03]" />
                  ))}
                </div>
                {/* Section: Plan steps */}
                <div className="space-y-2">
                  <div className="h-2.5 w-28 rounded loading-parallax bg-muted-foreground/[0.07]" />
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-10 w-full rounded-md border border-border/50 bg-muted-foreground/[0.03]" />
                  ))}
                </div>
              </div>
              {/* Right column */}
              <div className="space-y-4">
                {/* Stats 2×2 grid */}
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-16 rounded-lg border border-border/60 bg-muted/20" />
                  ))}
                </div>
                {/* Recent runs */}
                <div className="space-y-1.5">
                  <div className="h-2.5 w-24 rounded loading-parallax bg-muted-foreground/[0.07]" />
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-9 w-full rounded-md border border-border/50 bg-muted-foreground/[0.03]" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (agentQ.isError || !agentQ.data) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="font-mono text-sm text-destructive">Could not load agent.</p>
      </div>
    );
  }

  const { agent, runs } = agentQ.data;

  return (
    <div className="flex h-full flex-col gap-3 animate-fade-in">
      {/* Main panel */}
      <div className="flex flex-1 min-h-0 flex-col rounded-xl border border-border bg-card [overflow:clip]">
        {/* Agent header */}
        <div className="shrink-0 border-b border-border bg-muted/20 px-5 py-4 space-y-2">
          {/* Row 1: ‹ Agents  /  Name  +  Run Agent */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-1.5">
              <Link
                to="/agents"
                className="flex shrink-0 items-center gap-0.5 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeft className="h-3 w-3" />
                Agents
              </Link>
              <span className="text-muted-foreground/30">/</span>
              <h1 className="truncate text-xl font-bold tracking-tight">{agent.name}</h1>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {!agent.is_default && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditOpen(true)}
                  title="Edit this agent — saving re-selects its tools on the next run"
                >
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
              <Button
                variant="charcoal"
                size="sm"
                onClick={() => {
                  setActiveTab("run");
                  if (runPhase === "done") {
                    useAgentRunStore.getState().setPhase(agentId, "configure");
                    useAgentRunStore.getState().setEvents(agentId, []);
                  }
                  triggerContentAnimation();
                }}
              >
                <Play className="mr-1.5 h-3.5 w-3.5" />
                Run Agent
              </Button>
            </div>
          </div>

          {/* Row 2: badges + stats */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{agent.safety_level}</Badge>
              {(() => {
                const mt = String(
                  (agent.scope?.metadata_type as string | undefined) ??
                  ((agent.scope?.metadata_types as string[] | undefined)?.[0]) ??
                  ((agent.scope?.metadataTypes as string[] | undefined)?.[0]) ??
                  ""
                ).trim();
                return mt ? (
                  <Badge variant="secondary" className="font-mono text-[10px]">{mt}</Badge>
                ) : null;
              })()}
            </div>
            <div className="flex items-center gap-3 font-mono text-[11px] text-muted-foreground">
              <span><strong className="text-foreground">{agent.tools.length}</strong> tools</span>
              <span className="opacity-30">·</span>
              <span><strong className="text-foreground">{agent.plan.length}</strong> steps</span>
              <span className="opacity-30">·</span>
              <span>
                <strong className="text-foreground">{agent.success_count ?? 0}</strong>
                <span className="mx-0.5 opacity-40">/</span>
                <strong className="text-destructive">{agent.error_count ?? 0}</strong>
                {" "}runs
              </span>
            </div>
          </div>
        </div>

        {/* Live progress strip — always visible when running, above the tabs */}
        {runPhase === "running" && (
          <div className="shrink-0 border-b border-border bg-muted/10 px-5 py-2.5">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
                </span>
                <span className="truncate font-mono text-xs text-muted-foreground">{lastRunLabel || "Running…"}</span>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {runProgress.indeterminate
                    ? `${runProgress.completed} ${runProgress.completed === 1 ? "step" : "steps"} complete`
                    : `${runProgress.percent}%`}
                </span>
                <button
                  type="button"
                  onClick={() => useAgentRunStore.getState().stopRun(agentId)}
                  className="inline-flex items-center gap-1 rounded-md border border-destructive/50 px-2 py-0.5 font-mono text-[11px] text-destructive transition-colors hover:bg-destructive/10"
                >
                  Stop
                </button>
              </div>
            </div>
            <div className="h-0.5 overflow-hidden rounded-full bg-muted">
              {/* No declared total — an animated sliver that travels, rather than a width
                  derived from a denominator the backend never sent. */}
              {runProgress.indeterminate ? (
                <div className="h-full w-1/3 animate-[pulse_1.6s_ease-in-out_infinite] rounded-full bg-[hsl(var(--success))]/60" />
              ) : (
                <div
                  className="h-full rounded-full bg-[hsl(var(--success))] transition-all duration-500"
                  style={{ width: `${runProgress.percent}%` }}
                />
              )}
            </div>
          </div>
        )}

        {/* Tab strip */}
        <div className="flex shrink-0 overflow-x-auto border-b border-border">
          {(["overview", "config", "tools", "runs"] as const).map((tabId) => {
            const labels: Record<string, string> = {
              overview: "Overview", config: "Configuration",
              tools: "Tools & Capabilities", runs: "Run History",
            };
            return (
              <button
                key={tabId}
                type="button"
                onClick={() => {
                  if (runPhase === "done") {
                    useAgentRunStore.getState().setPhase(agentId, "configure");
                    useAgentRunStore.getState().setEvents(agentId, []);
                  }
                  setActiveTab(tabId);
                  triggerContentAnimation();
                }}
                className={[
                  "px-5 py-2.5 text-xs transition-colors border-b-2",
                  activeTab === tabId
                    ? "border-primary font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {labels[tabId]}
              </button>
            );
          })}
          {/* Separator */}
          <div className="mx-2 my-auto h-4 w-px bg-border/60" />
          {/* Run tab — inline with others, Play icon makes it recognisable */}
          <button
            type="button"
            onClick={() => {
              setActiveTab("run");
              if (runPhase === "done") {
                useAgentRunStore.getState().setPhase(agentId, "configure");
                useAgentRunStore.getState().setEvents(agentId, []);
              }
              triggerContentAnimation();
            }}
            className={[
              "flex items-center gap-1.5 px-4 py-2.5 text-xs transition-colors border-b-2",
              activeTab === "run"
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            ].join(" ")}
          >
            {runPhase === "running" ? (
              <>
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--success))]" />
                </span>
                Running
              </>
            ) : runPhase === "done" ? (
              <>
                <FileText className="h-3 w-3 shrink-0" />
                Report
              </>
            ) : (
              <>
                <Play className="h-3 w-3 shrink-0" />
                Run Agent
              </>
            )}
          </button>
        </div>

        {/* Tab content — animated on every tab / phase transition */}
        <animated.div style={contentSpring} className="flex flex-1 min-h-0">
          {/* Static tabs — conditionally rendered */}
          {activeTab === "overview" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <OverviewTab agent={agent} runs={runs} />
            </div>
          )}
          {activeTab === "config" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <ConfigTab agent={agent} />
            </div>
          )}
          {activeTab === "tools" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <ToolsTab agent={agent} />
            </div>
          )}
          {activeTab === "runs" && (
            <div className="flex-1 overflow-y-auto min-w-0">
              <RunsTab
                runs={runs}
                onViewRun={handleViewHistoricalRun}
                onSwitchToRunTab={() => setActiveTab("run")}
              />
            </div>
          )}
          {/* Run tab — always mounted so SSE stays alive when user switches tabs */}
          <div className={["flex flex-1 min-h-0 min-w-0 flex-col", activeTab === "run" ? "" : "hidden"].join(" ")}>
            <AgentRunTab
              agent={agent}
              viewRunId={viewRunId}
              onViewRunLoaded={() => setViewRunId(null)}
            />
          </div>
        </animated.div>
      </div>

      {/* Edit panel — reuses the create form in edit mode. Saving clears the
          crystallized recipe server-side, then drops the user on the run screen
          so the next run re-discovers tools for the edit. */}
      <CreateAgentPanel
        open={editOpen}
        agent={agent}
        onClose={() => setEditOpen(false)}
        onCreated={() => setEditOpen(false)}
        onUpdated={() => {
          setEditOpen(false);
          setActiveTab("run");
          useAgentRunStore.getState().setPhase(agentId, "configure");
          useAgentRunStore.getState().setEvents(agentId, []);
          triggerContentAnimation();
        }}
      />
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
// Mirrors the fields this page reads off `AgentRunRecord` (see
// services/agents/normalize.ts) — `error_message` included, so Run History can say
// why a run failed instead of only that it did.
type Run = {
  id: string;
  status: string;
  started_at: string;
  target_name?: string | null;
  total_ms?: number | null;
  error_message?: string | null;
};

function OverviewTab({ agent, runs }: { agent: AgentRecord; runs: Run[] }) {
  const outcome = agentOutcome(agent);
  return (
    <div className="grid gap-6 p-5 xl:grid-cols-[1fr_20rem]">
      {/* Left column */}
      <div className="space-y-6">
        {/* Goal */}
        <section>
          <SectionLabel>Agent Goal</SectionLabel>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{agent.goal}</p>
        </section>

        {/* Expected Outcome */}
        <section>
          <SectionLabel>Expected Outcome</SectionLabel>
          <Badge variant="outline" className="mt-2 mb-3">{outcome.title}</Badge>
          <div className="space-y-2">
            {outcome.bullets.map((item) => (
              <div key={item} className="rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
                {item}
              </div>
            ))}
          </div>
          {outcome.artifacts.length > 0 && (
            <div className="mt-3">
              <p className="mb-2 font-mono text-[11px] uppercase text-muted-foreground">Downloads created after run</p>
              <div className="flex flex-wrap gap-2">
                {outcome.artifacts.map((item) => (
                  <span key={item} className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Execution Plan */}
        <section>
          <SectionLabel>{agent.plan.length} step{agent.plan.length !== 1 ? "s" : ""} · Execution Plan</SectionLabel>
          <div className="mt-2 space-y-2">
            {agent.plan.map((step, idx) => (
              <div
                key={step.id || `step-${idx}`}
                className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3 rounded-md border border-border bg-muted/10 px-3 py-2.5"
              >
                <span className="font-mono text-[11px] text-muted-foreground pt-0.5">{idx + 1}.</span>
                <div>
                  <p className="font-mono text-xs font-semibold">{step.title}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Right column — sticky so it stays visible as left column scrolls */}
      <div className="sticky top-5 self-start space-y-5">
        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Tools" value={String(agent.tools.length)} icon={<Wrench className="h-3.5 w-3.5" />} />
          <StatCard label="Plan Steps" value={String(agent.plan.length)} icon={<FileText className="h-3.5 w-3.5" />} />
          <StatCard label="Safety" value={agent.safety_level} />
          <StatCard
            label="Runs"
            value={`${agent.success_count ?? 0} / ${agent.error_count ?? 0}`}
            icon={<CalendarClock className="h-3.5 w-3.5" />}
          />
        </div>

        {/* Recent runs */}
        <div>
          <SectionLabel>Recent Runs</SectionLabel>
          <div className="mt-2 space-y-1.5">
            {runs.slice(0, 6).map((run) => (
              <Link
                key={run.id}
                to={`/agents/${encodeURIComponent(agent.id)}?run_id=${encodeURIComponent(run.id)}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/10 px-3 py-2 hover:bg-muted/30 transition-colors"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="truncate font-mono text-xs">{formatRelative(run.started_at)}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {run.total_ms ? (
                    <span className="font-mono text-[10px] text-muted-foreground">{durationLabel(run.total_ms)}</span>
                  ) : null}
                  <RunStatusBadge status={run.status as RunStatusValue} />
                </div>
              </Link>
            ))}
            {runs.length === 0 && (
              <p className="font-mono text-xs text-muted-foreground py-2">No agent runs yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Configuration Tab ─────────────────────────────────────────────────────────
function ConfigTab({ agent }: { agent: AgentRecord }) {
  const scopeEntries = Object.entries(agent.scope || {}).filter(([, v]) => v);
  return (
    <div className="grid gap-6 p-5 xl:grid-cols-[1fr_20rem]">
      {/* Left: Prompt Contract */}
      <TermWindow title="Prompt Contract" status={`v${agent.prompt_version}`}>
        <p className="mb-2 font-mono text-[11px] uppercase text-muted-foreground">
          Stored prompt used for every run
        </p>
        <p className="overflow-auto whitespace-pre-wrap text-sm leading-6">
          {agent.prompt || "No prompt stored for this agent."}
        </p>
      </TermWindow>

      {/* Right: sticky sidebar — Agent Setup + Scope */}
      <div className="sticky top-5 self-start space-y-5">
        {/* Agent Setup */}
        <div>
          <SectionLabel>Agent Setup</SectionLabel>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <StatCard label="Model" value={String(agent.llm_config?.model || "default")} />
            <StatCard label="Max Tokens" value={String(agent.config?.max_tokens || "default")} />
            <StatCard label="Temperature" value={String(agent.config?.temperature ?? "default")} />
            <StatCard label="Prompt Version" value={`v${agent.prompt_version}`} />
          </div>
        </div>

        {/* Metadata Scope */}
        <div>
          <SectionLabel>Scope · {String(agent.scope?.mode || "org")}</SectionLabel>
          {scopeEntries.length === 0 ? (
            <p className="mt-2 font-mono text-xs text-muted-foreground">Org-wide — no target required.</p>
          ) : (
            <div className="mt-2 grid grid-cols-1 gap-2">
              {scopeEntries.map(([key, value]) => (
                <div key={key} className="rounded-md border border-border bg-muted/10 px-3 py-2">
                  <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                    {key.replace(/_/g, " ")}
                  </p>
                  <p className="mt-0.5 break-words text-sm font-semibold">
                    {Array.isArray(value)
                      ? value.join(", ")
                      : typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tools & Capabilities Tab ──────────────────────────────────────────────────
function ToolsTab({ agent }: { agent: AgentRecord }) {
  // Capabilities carry a friendly `label` (e.g. "Apex Source") plus `name`, which
  // holds the underlying TOOL CODE (e.g. "get_class_source"). The old render used
  // `cap.name` as the primary text — i.e. it showed the code, identical to the
  // Tools column. Show the friendly label as primary and the code as subtext, and
  // only render the column when a label actually adds info beyond the raw code
  // (otherwise it just duplicates Tools).
  const capLabel = (cap: Record<string, unknown>) =>
    String(cap.label || cap.title || cap.name || cap.code || cap.tool || "");
  const capCode = (cap: Record<string, unknown>) =>
    String(cap.name || cap.tool || cap.code || "");
  const capabilities = agent.capabilities ?? [];
  const showCapabilities = capabilities.some((cap) => capLabel(cap) && capLabel(cap) !== capCode(cap));

  return (
    <div className={["grid gap-5 p-5", showCapabilities ? "xl:grid-cols-2" : ""].join(" ")}>
      {showCapabilities && (
      <div>
        <SectionLabel>{capabilities.length} Capabilities</SectionLabel>
        <div className="mt-2 space-y-1.5">
          {capabilities.map((cap, idx) => (
            <div
              key={`${capCode(cap) || capLabel(cap) || idx}`}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/10 px-3 py-2"
            >
              <Settings2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="break-words font-mono text-xs">{capLabel(cap)}</p>
                {Boolean(capCode(cap)) && capCode(cap) !== capLabel(cap) && (
                  <p className="mt-0.5 break-words font-mono text-[10px] text-muted-foreground">
                    {capCode(cap)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      <div>
        <SectionLabel>{activeAgentTools(agent.tools).length} Tools</SectionLabel>
        <div className="mt-2 space-y-1.5">
          {agent.tools.map((tool) => {
            // A retired tool is still persisted on older agents. Show it struck through
            // and labelled rather than letting it read as a working capability.
            const retired = isRetiredAgentTool(tool);
            return (
              <div
                key={tool}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
                  retired ? "border-dashed border-border/60 bg-muted/5" : "border-border bg-muted/10"
                }`}
                title={retired ? RETIRED_AGENT_TOOL_REASON : undefined}
              >
                <Wrench className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span
                  className={`min-w-0 flex-1 break-words font-mono text-xs ${
                    retired ? "text-muted-foreground line-through" : ""
                  }`}
                >
                  {tool}
                </span>
                {retired ? (
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
                    retired
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Run History Tab ───────────────────────────────────────────────────────────
function RunsTab({
  runs,
  onViewRun,
  onSwitchToRunTab,
}: {
  runs: Run[];
  onViewRun: (runId: string) => void;
  onSwitchToRunTab: () => void;
}) {
  if (runs.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-20 text-center">
        <CalendarClock className="mb-3 h-10 w-10 text-muted-foreground/25" />
        <p className="font-semibold">No runs yet</p>
        <p className="mt-1 text-sm text-muted-foreground">Run this agent to see history here.</p>
        <Button variant="charcoal" size="sm" className="mt-4" onClick={onSwitchToRunTab}>
          <Play className="mr-1.5 h-3.5 w-3.5" />
          Run Agent
        </Button>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border">
          <th className="py-3 pl-5 pr-4 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Time</th>
          <th className="py-3 pr-4 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Target</th>
          <th className="py-3 pr-4 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Status</th>
          <th className="py-3 pr-4 text-left font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Duration</th>
          <th className="py-3 pr-5 text-right font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Actions</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => {
          const errorLabel = runErrorLabel(run.error_message);
          return (
          <tr key={run.id} className="border-b border-border/50 last:border-0 transition-colors hover:bg-muted/20">
            <td className="py-3 pl-5 pr-4 font-mono text-xs">{formatRelative(run.started_at)}</td>
            <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
              {run.target_name && run.target_name !== "__all__" ? run.target_name : "Org-wide"}
            </td>
            <td className="py-3 pr-4">
              <RunStatusBadge status={run.status as RunStatusValue} />
              {errorLabel && (
                <p className="mt-1 max-w-[22rem] font-mono text-[10px] leading-snug text-destructive">
                  {errorLabel}
                </p>
              )}
            </td>
            <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
              {durationLabel(run.total_ms)}
            </td>
            <td className="py-3 pr-5 text-right">
              <button
                type="button"
                onClick={() => onViewRun(run.id)}
                className="font-mono text-[11px] text-primary hover:underline"
              >
                View report
              </button>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

// ── Small shared atoms ────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon?: ReactNode }) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-1.5">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      </div>
      <p className="truncate text-sm font-semibold">{value}</p>
    </div>
  );
}
